import React, { useEffect, useRef } from 'react';
import PlainModal from 'components/PlainModal';
import api from 'api';
import { Analytics, getPlatformAndVersion } from 'utils/analytics';
import { isIosNative, isNativeApp, openExternalUrl } from 'utils/nativeApp';
import styles from './ReviewAskModal.module.scss';

export type ReviewAskPayload = {
  solicitationId: string;
  urls: {
    ios_native: string;
    android_native: string;
    web: string;
  };
  triggerRule?: string;
  destinations?: {
    ios_native?: string;
    android_native?: string;
    web?: string;
  };
};

export type ReviewAskRole = 'dentist' | 'hygienist';

type PlatformKey = 'ios' | 'android' | 'web';

function resolvePlatformKey(): PlatformKey {
  if (isIosNative()) return 'ios';
  if (isNativeApp()) return 'android';
  return 'web';
}

export function resolveReviewAskDestination(reviewAsk: ReviewAskPayload): {
  platform: PlatformKey;
  destination: string;
  url: string;
  ctaLabel: string;
} {
  const platform = resolvePlatformKey();
  if (platform === 'ios') {
    return {
      platform,
      destination: reviewAsk.destinations?.ios_native || 'appstore',
      url: reviewAsk.urls.ios_native,
      ctaLabel: 'Review on the App Store',
    };
  }
  if (platform === 'android') {
    return {
      platform,
      destination: reviewAsk.destinations?.android_native || 'play',
      url: reviewAsk.urls.android_native,
      ctaLabel: 'Review on Google Play',
    };
  }
  return {
    platform,
    destination: reviewAsk.destinations?.web || 'google',
    url: reviewAsk.urls.web,
    ctaLabel: 'Review us on Google',
  };
}

function trackReviewAsk(
  event:
    | 'review_ask_shown'
    | 'review_ask_clicked'
    | 'review_ask_not_now'
    | 'review_ask_never',
  role: ReviewAskRole,
  reviewAsk: ReviewAskPayload,
  destination: string,
) {
  const { platform } = getPlatformAndVersion();
  Analytics.track(event, {
    role,
    platform,
    destination,
    trigger_rule: reviewAsk.triggerRule || 'five_or_favourite',
    solicitation_id: reviewAsk.solicitationId,
  });
}

export interface ReviewAskModalProps {
  open: boolean;
  reviewAsk: ReviewAskPayload | null;
  role: ReviewAskRole;
  onDone: () => void;
  /** Optional owner fence for a route/account-bound solicitation. */
  isCurrent?: () => boolean;
}

/**
 * Shared DO + Talent review solicitation modal (T2-6899).
 * Records shown once on open; CTA opens URL even if clicked write fails.
 */
const ReviewAskModal: React.FC<ReviewAskModalProps> = ({
  open,
  reviewAsk,
  role,
  onDone,
  isCurrent,
}) => {
  const shownRecordedRef = useRef<string | null>(null);

  const resolved = reviewAsk ? resolveReviewAskDestination(reviewAsk) : null;

  useEffect(() => {
    if (!open || !reviewAsk || !resolved || isCurrent?.() === false) return;
    if (shownRecordedRef.current === reviewAsk.solicitationId) return;
    shownRecordedRef.current = reviewAsk.solicitationId;

    trackReviewAsk('review_ask_shown', role, reviewAsk, resolved.destination);
    void api.postReviewSolicitationOutcome({
      solicitationId: reviewAsk.solicitationId,
      action: 'shown',
      channel: resolved.destination as 'appstore' | 'play' | 'google',
    }).catch(() => { /* fire-and-forget */ });
  }, [open, reviewAsk, role, resolved, isCurrent]);

  if (!reviewAsk || !resolved) return null;

  const postOutcome = async (action: 'clicked' | 'not_now' | 'never') => {
    if (isCurrent?.() === false) return;
    try {
      await api.postReviewSolicitationOutcome({
        solicitationId: reviewAsk.solicitationId,
        action,
        channel: resolved.destination as 'appstore' | 'play' | 'google',
      });
    } catch {
      // CTA still opens below; not_now/never still exit.
    }
  };

  const handleCta = async () => {
    if (isCurrent?.() === false) return;
    trackReviewAsk('review_ask_clicked', role, reviewAsk, resolved.destination);
    await postOutcome('clicked');
    if (isCurrent?.() === false) return;
    openExternalUrl(resolved.url);
    onDone();
  };

  const handleNotNow = async () => {
    if (isCurrent?.() === false) return;
    trackReviewAsk('review_ask_not_now', role, reviewAsk, resolved.destination);
    await postOutcome('not_now');
    if (isCurrent?.() === false) return;
    onDone();
  };

  const handleNever = async () => {
    if (isCurrent?.() === false) return;
    trackReviewAsk('review_ask_never', role, reviewAsk, resolved.destination);
    await postOutcome('never');
    if (isCurrent?.() === false) return;
    onDone();
  };

  return (
    <PlainModal
      isOpen={open}
      onClose={() => { void handleNotNow(); }}
      ariaLabel="Review TempStars"
      contentDesc="review-ask-modal"
      dataTestId="review-ask-modal"
      size="xs"
      disableBackdropClose
      disableEscapeKeyDown
      dialogClassName={styles.dialog}
    >
      <h2 className={styles.title}>That&apos;s what we love to hear!</h2>
      <p className={styles.body}>
        Reviews from members like you help more offices and dental professionals
        discover TempStars. Would you share the love?
      </p>
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.primary}
          data-testid="review-ask-cta"
          aria-label={resolved.ctaLabel}
          {...({ 'content-desc': 'review-ask-cta' } as any)}
          onClick={() => { void handleCta(); }}
        >
          {resolved.ctaLabel}
        </button>
        <button
          type="button"
          className={styles.secondary}
          data-testid="review-ask-not-now"
          aria-label="Not now"
          {...({ 'content-desc': 'review-ask-not-now' } as any)}
          onClick={() => { void handleNotNow(); }}
        >
          Not now
        </button>
        <button
          type="button"
          className={styles.never}
          data-testid="review-ask-never"
          aria-label="Don't ask me again"
          {...({ 'content-desc': 'review-ask-never' } as any)}
          onClick={() => { void handleNever(); }}
        >
          Don&apos;t ask me again
        </button>
      </div>
    </PlainModal>
  );
};

export default ReviewAskModal;
